import type { ReactNode } from "react";
import { CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";
import type { StripePushOutcome } from "@/application/merchants/business_info_edit";

export interface AcceptedCopy {
  headline: string;
  detail: string;
}

interface Props {
  outcome: StripePushOutcome | "checking";
  /** What an accepted push says; the other outcomes share their copy. */
  acceptedCopy: AcceptedCopy;
  /** What the checking state says is already in place; it differs by what the dialog edits. */
  checkingDetail: string;
}

const RESULT_COPY: Record<
  StripePushOutcome["kind"] | "checking",
  { icon: ReactNode; className: string; headline: string | null }
> = {
  checking: {
    icon: <Loader2 className="h-4 w-4 animate-spin" />,
    className: "bg-muted border-border text-foreground",
    headline: "Saved. Checking Stripe…",
  },
  accepted: {
    icon: <CheckCircle2 className="h-4 w-4" />,
    className: "border-green-200 bg-green-50 text-green-800",
    headline: null,
  },
  rejected: {
    icon: <XCircle className="h-4 w-4" />,
    className: "border-red-200 bg-red-50 text-red-800",
    headline: "Saved, but Stripe didn't take the change.",
  },
  noAnswer: {
    icon: <Clock className="h-4 w-4" />,
    className: "bg-muted border-border text-foreground",
    headline: "Saved. Stripe hasn't confirmed yet.",
  },
};

/** The Stripe outcome of a saved info edit: checking, accepted, rejected or unanswered. */
export function StripeResult({ outcome, acceptedCopy, checkingDetail }: Props) {
  const kind = outcome === "checking" ? "checking" : outcome.kind;
  // eslint-disable-next-line security/detect-object-injection -- kind is a RESULT_COPY key by construction
  const copy = RESULT_COPY[kind];
  const headline = copy.headline ?? acceptedCopy.headline;
  const detail =
    outcome === "checking"
      ? checkingDetail
      : outcome.kind === "accepted"
        ? acceptedCopy.detail
        : outcome.kind === "rejected"
          ? `Stripe said: ${outcome.message.replace(/\.+$/, "")}. Check the details and save again.`
          : "Check the profile again shortly.";
  return (
    <div
      className={`flex gap-2 rounded-md border p-3 text-sm ${copy.className}`}
    >
      <span className="mt-0.5 shrink-0">{copy.icon}</span>
      <div>
        <p className="font-medium">{headline}</p>
        <p className="text-xs opacity-90">{detail}</p>
      </div>
    </div>
  );
}
